import { useContext, useEffect, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Navigation from "./components/navigation";
import UserAvatar from "./components/UserAvatar";
import StoreSwitcher from "./components/StoreSwitcher";
import AppSwitcherPopover from "./components/appSwitcherPopover";
import Alert from "./components/alert";
import useStoreSetupStatus from "./hooks/useStoreSetupStatus";
import { MyContext } from "./context";

import logo from "./images/co_logo.png";

const SETUP_PATH = "/settings/setup";

const Layout = () => {
  const { activeStoreId, loading, allDone } = useStoreSetupStatus();
  const { stores } = useContext(MyContext);
  const activeStore = stores?.find((s) => s.storeId === activeStoreId);
  const isTestModeStore = (activeStore?.mode || 'test') === 'test';
  const location = useLocation();
  const navigate = useNavigate();
  // Only auto-redirect to setup from the Home page, and only once per store
  // switch, right when that store's status finishes loading -- every other
  // link/page in the app should just work, even while the active store is
  // incomplete (e.g. following an error message's link to a specific tab to
  // fix something). Without the store-switch guard, the redirect would
  // re-fire on every Home visit (since `allDone` stays false) for a store
  // that's never going to be fully set up.
  const checkedStoreIdRef = useRef(null);

  useEffect(() => {
    if (loading || activeStoreId == null) return;
    if (checkedStoreIdRef.current === activeStoreId) return;
    checkedStoreIdRef.current = activeStoreId;
    if (!allDone && location.pathname === "/") navigate(SETUP_PATH);
  }, [activeStoreId, loading, allDone, location.pathname, navigate]);

  return (
    <>
      <div>
        <nav className="bg-white border-b border-gray-200 fixed z-30 w-full">
          <div className="px-3 py-3 lg:pr-5">
            <div className="flex items-center justify-between">
              <div className="flex w-full items-center justify-start justify-between">
                <button
                  id="toggleSidebarMobile"
                  aria-expanded="true"
                  aria-controls="sidebar"
                  className="lg:hidden mr-2 text-gray-600 hover:cursor-pointer p-2 hover:bg-gray-100 focus:bg-gray-100 focus:ring-2 focus:ring-gray-100 rounded"
                >
                  <svg
                    id="toggleSidebarMobileHamburger"
                    className="w-6 h-6"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                </button>
                <a
                  href="/"
                  className="text-base font-bold flex items-center lg:ml-1.5"
                >
                  <img src={logo} className="h-10" alt="PI eCommerce Admin" />
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <StoreSwitcher />
                <UserAvatar />
                <AppSwitcherPopover />
              </div>
            </div>
          </div>
        </nav>



        <div className='flex overflow-hidden bg-white pt-16'>
          <Navigation />
          <div className="bg-white opacity-50 hidden fixed inset-0 z-10"></div>
          <div
            id="main-content"
            className="h-full w-full bg-white relative overflow-y-auto lg:ml-64"
            style={{ minHeight: "100vh" }}
          >
            <main>
              <div className="bg-white h-screenx pt-0 pl-12 m-w-4xl">
                <div>
                  {isTestModeStore && (
                    <div className="w-full mb-4 -ml-12">
                      <Alert
                        alert={{
                          type: "testMode",
                          message: "This store is in Test Mode - orders don't charge customers.",
                        }}
                      />
                    </div>
                  )}
                  <Outlet />
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default Layout;
